import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PenLine, CheckCircle, Clock, XCircle } from 'lucide-react';
import useAuthStore from '../../context/authStore';

const sigStatusLabels = {
  pending: 'Awaiting Your Signature',
  viewed: 'Opened — Not Yet Signed',
  signed: 'Signed',
  declined: 'Declined',
  revision_requested: 'Revision Requested',
};

export default function SignaturesPage() {
  const { token } = useAuthStore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('25');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await fetch('/api/documents/for-signing', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Failed to fetch signature documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const sigBadge = (sigStatus) => {
    const styles = {
      pending:            'bg-yellow-100 text-yellow-700',
      viewed:             'bg-blue-100 text-blue-700',
      signed:             'bg-green-100 text-green-700',
      declined:           'bg-red-100 text-red-700',
      revision_requested: 'bg-orange-100 text-orange-700',
    };
    return styles[sigStatus] || 'bg-gray-100 text-gray-700';
  };

  const filteredDocs = filter === 'all'
    ? documents
    : documents.filter(d => d.finalSignatureStatus === filter);

  useEffect(() => {
    setPage(1);
  }, [filter, pageSize, documents.length]);

  const pagination = useMemo(() => {
    const total = filteredDocs.length;
    if (pageSize === 'all') {
      return { total, pages: 1, currentPage: 1, items: filteredDocs };
    }
    const limit = Math.max(1, Number(pageSize) || 25);
    const pages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.min(page, pages);
    const start = (currentPage - 1) * limit;
    return {
      total,
      pages,
      currentPage,
      items: filteredDocs.slice(start, start + limit),
    };
  }, [filteredDocs, page, pageSize]);

  const pending = documents.filter(d => d.finalSignatureStatus === 'pending' || d.finalSignatureStatus === 'viewed');
  const signed  = documents.filter(d => d.finalSignatureStatus === 'signed');
  const declined = documents.filter(d => d.finalSignatureStatus === 'declined');

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Signatures</h1>
        <p className="text-gray-600 mt-1">Documents awaiting or completed your signature</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-5 flex items-center gap-4">
          <div className="bg-yellow-50 rounded-lg p-2.5 shrink-0">
            <Clock size={22} className="text-yellow-500" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Awaiting Signature</div>
            <div className="text-3xl font-bold text-yellow-600 leading-tight">{pending.length}</div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5 flex items-center gap-4">
          <div className="bg-green-50 rounded-lg p-2.5 shrink-0">
            <CheckCircle size={22} className="text-green-500" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Signed</div>
            <div className="text-3xl font-bold text-green-600 leading-tight">{signed.length}</div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5 flex items-center gap-4">
          <div className="bg-red-50 rounded-lg p-2.5 shrink-0">
            <XCircle size={22} className="text-red-400" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Declined</div>
            <div className="text-3xl font-bold text-red-600 leading-tight">{declined.length}</div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {['all', 'pending', 'viewed', 'signed', 'declined', 'revision_requested'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`py-2 px-2 rounded-lg text-sm font-medium text-center transition-colors ${
                filter === s ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s === 'all' ? 'All' : sigStatusLabels[s] || s}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white border rounded-xl p-10 text-center text-gray-500">Loading...</div>
      ) : filteredDocs.length === 0 ? (
        <div className="bg-white border rounded-xl p-10 text-center">
          <PenLine size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No signature requests found.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border rounded-xl p-4 mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">Showing {pagination.items.length} of {pagination.total} signature requests</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Show all</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          {/* Desktop table — hidden on mobile */}
          <div className="hidden sm:block bg-white border rounded-xl overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Document</th>
                  <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Category</th>
                  <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Signed On</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pagination.items.map((doc) => (
                  <tr key={doc._id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <Link
                        to={`/documents/draft/${doc._id}`}
                        className="font-medium text-green-600 hover:underline"
                      >
                        {doc.name}
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm capitalize">
                      {doc.category?.replace(/_/g, ' ') || '—'}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${sigBadge(doc.finalSignatureStatus)}`}>
                        {sigStatusLabels[doc.finalSignatureStatus] || doc.finalSignatureStatus || 'Pending'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm">
                      {doc.finalSignedAt ? new Date(doc.finalSignedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards — shown only on small screens */}
          <div className="sm:hidden space-y-3">
            {pagination.items.map((doc) => (
              <div key={doc._id} className="bg-white border rounded-xl p-4">
                <Link
                  to={`/documents/draft/${doc._id}`}
                  className="font-semibold text-green-600 hover:underline block mb-2"
                >
                  {doc.name}
                </Link>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 capitalize">
                    {doc.category?.replace(/_/g, ' ') || '—'}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${sigBadge(doc.finalSignatureStatus)}`}>
                    {sigStatusLabels[doc.finalSignatureStatus] || doc.finalSignatureStatus || 'Pending'}
                  </span>
                </div>
                {doc.finalSignedAt && (
                  <p className="text-xs text-gray-400 mt-2">
                    Signed {new Date(doc.finalSignedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
          {pagination.pages > 1 && pageSize !== 'all' && (
            <div className="bg-white border rounded-xl p-4 mt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.pages}</span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(pagination.pages, prev + 1))}
                disabled={pagination.currentPage === pagination.pages}
                className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
